import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft, IconShieldCheck } from '@iwoca/icons';
import { AlertBox, Button, IwocaLogo, Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';

import styles from './BankSelectionModal.module.css';
import { useGetUserConsent } from '../../../../api/lendingApi/lendingApiQueries';
import { useUserConsentMutation } from '../../../../api/userConsentApi/useUserConsentMutation';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';
import Spinner from '../../../Spinner/Spinner';
import BankLinksSVG from '../BankLinkSVG/BankLinkSVG';
import { TOnlineBank } from '../OpenBanking.types';

type TProps = {
  isOpen: boolean;
  onClickBack: () => void;
  handleCloseWithCallback: (closeCallback?: () => void) => void;
  selectedBank: TOnlineBank;
  bankUrlError: boolean;
  bankAuthURL: string;
  shouldSkipPermissionView: boolean;
  sharedLinkBusinessName?: string;
};

const TRUE_LAYER_OPEN_BANKING_PROVIDER = 'truelayer_open_banking';
const BankConnectionView = ({
  isOpen,
  onClickBack,
  handleCloseWithCallback,
  selectedBank,
  bankUrlError,
  bankAuthURL,
  shouldSkipPermissionView,
  sharedLinkBusinessName,
}: TProps) => {
  const { t } = useLingui();

  const stateKey = useContextStateKey();
  const { userConsent } = useGetUserConsent();
  const { postUserConsent } = useUserConsentMutation();

  useEffect(() => {
    if (shouldSkipPermissionView && bankAuthURL) {
      setTimeout(() => {
        // set timeout to show redirecting
        if (isOpen) {
          const closeCallback = () => {
            window.location.href = bankAuthURL;
          };
          handleCloseWithCallback(closeCallback);
        }
      }, 2000);
    }
  }, [shouldSkipPermissionView, bankAuthURL]);

  const handleBankConnectionCTA = () => {
    tracking.ampli.trueLayerBankConnectStarted({
      ...getAmplitudeDefaultEventProperties(),
      bankId: selectedBank?.bank_id as string,
      bankName: selectedBank?.bank_name,
    });

    if (!userConsent) {
      postUserConsent({
        body: {
          data: {
            bundle_hash: viteBuildHash,
          },
        },
        provider: TRUE_LAYER_OPEN_BANKING_PROVIDER,
        stateKey: stateKey as string,
      });
    }
    window.location.href = bankAuthURL;
  };

  const selectedBankName = selectedBank.bank_name;

  if (shouldSkipPermissionView) {
    if (bankUrlError && selectedBank) {
      return (
        <AlertBox variant="failure">
          <p className={styles.bankUrlError}>
            <Trans>
              Sorry, we ran into an issue while retrieving the link for{' '}
              {selectedBankName}. Please try again later.
            </Trans>
          </p>
        </AlertBox>
      );
    }

    return (
      <div className={styles.bankRedirect}>
        <Spinner />
        <p className={styles.redirectText}>
          <Trans>Redirecting you to our open banking provider...</Trans>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.bankConnectionHeader}>
        <Button
          variant="tertiary"
          onClick={onClickBack}
          className={styles.backButton}
        >
          <IconAngleLeft />
          <Trans>Back</Trans>
        </Button>
        <div className={styles.bankConnectionLogos}>
          <IwocaLogo iconOnly className={styles.iwocaLogo} />
          <BankLinksSVG classNames={styles.bankLink} />
          <figure className={styles.bankConnectionLogo}>
            <img
              src={selectedBank?.logo_url}
              alt={selectedBank?.bank_name}
              className={styles.bankLogo}
            />
          </figure>
        </div>
      </div>
      <section className={styles.bankConsentContainer}>
        <h3>
          <strong>
            <Trans>What details am I sharing?</Trans>
          </strong>
        </h3>
        {sharedLinkBusinessName && (
          <p>
            <Trans>
              {sharedLinkBusinessName} has already agreed for the following
              information to be shared with iwoca.
            </Trans>
          </p>
        )}
        <p>
          <Trans>
            The following information will be shared with iwoca LTD:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>Full name</Trans>
          </li>
          <li>
            <Trans>Account number and sort code</Trans>
          </li>
          <li>
            <Trans>Account balance</Trans>
          </li>
          <li>
            <Trans>
              Transaction history, direct debits and standing orders
            </Trans>
          </li>
        </ul>
        <div className={styles.bankConnectionGradeContainer}>
          <div className={styles.bankGradeContainer}>
            <IconShieldCheck className={styles.bankGradeIcon} />
            <span>
              <Trans>Bank-grade encryption</Trans>
            </span>
          </div>
          <p>
            <Trans>
              We use a partner called Truelayer, to access the information
              needed to assess your ability to repay a loan.
            </Trans>
          </p>
          <p>
            <Trans>
              Truelayer needs your permission to access your information for 90
              days.
            </Trans>
          </p>
          <p>
            <Trans>
              Truelayer is FCA regulated, and won’t share or use your personal
              data for anything else.
            </Trans>
          </p>
        </div>
        <div
          className={styles.trueLayerFooter}
          data-testid="true-layer-modal-footer"
        >
          <p>
            <small>
              <Trans>
                By tapping ‘Allow’, you agree to{' '}
                <Link
                  data-testid="terms-of-service-link"
                  href={t`https://truelayer.com/legal/enduser_tos/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Truelayer’s Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  data-testid="privacy-policy-link"
                  href={t`https://truelayer.com/legal/privacy/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </Trans>
            </small>
          </p>
        </div>
      </section>
      {bankUrlError && selectedBank && (
        <AlertBox variant="failure">
          <p className={styles.bankUrlError}>
            <Trans>
              Sorry, we ran into an issue while retrieving the link for{' '}
              {selectedBankName}. Please try again later.
            </Trans>
          </p>
        </AlertBox>
      )}
      <div className={styles.ctaContainer}>
        <Button
          data-testid="truelayer-bank-url"
          onClick={handleBankConnectionCTA}
          rel="noopener noreferrer"
          loading={!bankAuthURL && !bankUrlError}
          disabled={Boolean(bankUrlError)}
          aria-disabled={bankUrlError}
          size="large"
        >
          <Trans>Allow</Trans>
        </Button>
        <Button
          variant="tertiary"
          onClick={() => handleCloseWithCallback()}
          size="large"
        >
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </>
  );
};

export default BankConnectionView;
